import * as THREE from "three";
import BaseThreeScene from "../../scenes/BaseThreeScene";
import { createSeatBlock } from "../../scenes/arrayPrimitives";
import {
    createMatrixBoardBase,
    createMatrixCellFrame,
} from "../../scenes/matrixPrimitives";
import {
    createTextSprite,
    updateTextSprite,
} from "../../scenes/shared/textSpritePrimitives";

const DEFAULT_MATRIX = [
    [5, 1, 9, 11],
    [2, 4, 8, 10],
    [13, 3, 6, 7],
    [15, 14, 12, 16],
];

const BASE_CELL_COLOR = new THREE.Color(0x1e293b);
const COMPLETE_CELL_COLOR = new THREE.Color(0x14532d);
const TRANSPOSE_PHASE_COLOR = new THREE.Color(0x38bdf8);
const REFLECT_PHASE_COLOR = new THREE.Color(0x22c55e);
const LAYER_PHASE_COLOR = new THREE.Color(0xa855f7);

const ACTIVE_A_COLOR = new THREE.Color(0x60a5fa);
const ACTIVE_B_COLOR = new THREE.Color(0xf59e0b);
const ACTIVE_RIGHT_COLOR = new THREE.Color(0x22c55e);
const ACTIVE_LEFT_COLOR = new THREE.Color(0xa855f7);

function cloneMatrix(matrix) {
    return matrix.map((row) => row.slice());
}

function normalizeMatrixInput(input) {
    if (!Array.isArray(input) || input.length === 0) {
        return cloneMatrix(DEFAULT_MATRIX);
    }

    return input.map((row) =>
        Array.isArray(row) ? row.map((value) => Number(value)) : []
    );
}

function formatCell(cell) {
    if (!cell) {
        return "-";
    }

    return `(${cell.row}, ${cell.col})`;
}

function formatPair(pair) {
    if (!pair) {
        return "-";
    }

    return `A ${formatCell(pair.a)}  B ${formatCell(pair.b)}`;
}

function formatPairValues(values) {
    if (!values) {
        return "-";
    }

    return `A:${values.a}  B:${values.b}`;
}

function formatCycleCells(positions) {
    if (!positions) {
        return "-";
    }

    return `T ${formatCell(positions.top)}  R ${formatCell(
        positions.right
    )}  B ${formatCell(positions.bottom)}  L ${formatCell(positions.left)}`;
}

function formatCycleValues(values) {
    if (!values) {
        return "-";
    }

    return `T:${values.top}  R:${values.right}  B:${values.bottom}  L:${values.left}`;
}

function createActiveMapForPair(pair) {
    const activeMap = new Map();

    if (!pair) {
        return activeMap;
    }

    activeMap.set(`${pair.a.row},${pair.a.col}`, "A");
    activeMap.set(`${pair.b.row},${pair.b.col}`, "B");

    return activeMap;
}

function createActiveMapForCycle(positions) {
    const activeMap = new Map();

    if (!positions) {
        return activeMap;
    }

    activeMap.set(`${positions.top.row},${positions.top.col}`, "T");
    activeMap.set(`${positions.right.row},${positions.right.col}`, "R");
    activeMap.set(`${positions.bottom.row},${positions.bottom.col}`, "B");
    activeMap.set(`${positions.left.row},${positions.left.col}`, "L");

    return activeMap;
}

function getTagColor(tag) {
    if (tag === "A" || tag === "T") {
        return ACTIVE_A_COLOR;
    }

    if (tag === "R") {
        return ACTIVE_RIGHT_COLOR;
    }

    if (tag === "L") {
        return ACTIVE_LEFT_COLOR;
    }

    return ACTIVE_B_COLOR;
}

function getPhaseColor(algorithm, phase) {
    if (algorithm === "transpose-reflect") {
        return phase === "reflect" ? REFLECT_PHASE_COLOR : TRANSPOSE_PHASE_COLOR;
    }

    return LAYER_PHASE_COLOR;
}

function buildTransposeReflectSteps(matrix) {
    const n = matrix.length;
    const workingMatrix = cloneMatrix(matrix);
    const transposeTotal = (n * (n - 1)) / 2;
    const reflectTotal = n * Math.floor(n / 2);

    const steps = [
        {
            type: "start",
            algorithm: "transpose-reflect",
            matrix: cloneMatrix(workingMatrix),
            size: n,
        },
        {
            type: "algorithm-start",
            algorithm: "transpose-reflect",
            matrix: cloneMatrix(workingMatrix),
        },
        {
            type: "phase-start",
            algorithm: "transpose-reflect",
            phase: "transpose",
            matrix: cloneMatrix(workingMatrix),
        },
    ];

    let transposeSwapIndex = 0;
    let reflectSwapIndex = 0;

    for (let row = 0; row < n; row += 1) {
        for (let col = row + 1; col < n; col += 1) {
            const pair = {
                a: { row, col },
                b: { row: col, col: row },
            };

            const valuesBefore = {
                a: workingMatrix[pair.a.row][pair.a.col],
                b: workingMatrix[pair.b.row][pair.b.col],
            };

            steps.push({
                type: "preview-swap",
                algorithm: "transpose-reflect",
                phase: "transpose",
                pair,
                row,
                valuesBefore,
                valuesAfter: {
                    a: valuesBefore.b,
                    b: valuesBefore.a,
                },
                matrix: cloneMatrix(workingMatrix),
                swapIndex: transposeSwapIndex + 1,
            });

            const temp = workingMatrix[pair.a.row][pair.a.col];
            workingMatrix[pair.a.row][pair.a.col] =
                workingMatrix[pair.b.row][pair.b.col];
            workingMatrix[pair.b.row][pair.b.col] = temp;

            transposeSwapIndex += 1;

            steps.push({
                type: "apply-swap",
                algorithm: "transpose-reflect",
                phase: "transpose",
                pair,
                row,
                valuesBefore,
                valuesAfter: {
                    a: workingMatrix[pair.a.row][pair.a.col],
                    b: workingMatrix[pair.b.row][pair.b.col],
                },
                matrix: cloneMatrix(workingMatrix),
                swapIndex: transposeSwapIndex,
            });
        }
    }

    steps.push({
        type: "phase-start",
        algorithm: "transpose-reflect",
        phase: "reflect",
        matrix: cloneMatrix(workingMatrix),
    });

    for (let row = 0; row < n; row += 1) {
        for (let col = 0; col < Math.floor(n / 2); col += 1) {
            const pair = {
                a: { row, col },
                b: { row, col: n - 1 - col },
            };

            const valuesBefore = {
                a: workingMatrix[pair.a.row][pair.a.col],
                b: workingMatrix[pair.b.row][pair.b.col],
            };

            steps.push({
                type: "preview-swap",
                algorithm: "transpose-reflect",
                phase: "reflect",
                pair,
                row,
                valuesBefore,
                valuesAfter: {
                    a: valuesBefore.b,
                    b: valuesBefore.a,
                },
                matrix: cloneMatrix(workingMatrix),
                swapIndex: reflectSwapIndex + 1,
            });

            const temp = workingMatrix[pair.a.row][pair.a.col];
            workingMatrix[pair.a.row][pair.a.col] =
                workingMatrix[pair.b.row][pair.b.col];
            workingMatrix[pair.b.row][pair.b.col] = temp;

            reflectSwapIndex += 1;

            steps.push({
                type: "apply-swap",
                algorithm: "transpose-reflect",
                phase: "reflect",
                pair,
                row,
                valuesBefore,
                valuesAfter: {
                    a: workingMatrix[pair.a.row][pair.a.col],
                    b: workingMatrix[pair.b.row][pair.b.col],
                },
                matrix: cloneMatrix(workingMatrix),
                swapIndex: reflectSwapIndex,
            });
        }
    }

    steps.push({
        type: "complete",
        algorithm: "transpose-reflect",
        matrix: cloneMatrix(workingMatrix),
    });

    return {
        steps,
        resultMatrix: cloneMatrix(workingMatrix),
        metadata: {
            algorithm: "transpose-reflect",
            algorithmLabel: "Transpose + Reflect",
            totalMoves: transposeTotal + reflectTotal,
            phaseTotals: {
                transpose: transposeTotal,
                reflect: reflectTotal,
            },
        },
    };
}

function buildLayerCycleSteps(matrix) {
    const n = matrix.length;
    const workingMatrix = cloneMatrix(matrix);

    const steps = [
        {
            type: "start",
            algorithm: "layer-cycle",
            matrix: cloneMatrix(workingMatrix),
            size: n,
        },
        {
            type: "algorithm-start",
            algorithm: "layer-cycle",
            matrix: cloneMatrix(workingMatrix),
        },
    ];

    let totalCycles = 0;

    for (let layer = 0; layer < Math.floor(n / 2); layer += 1) {
        const first = layer;
        const last = n - 1 - layer;

        steps.push({
            type: "layer-start",
            algorithm: "layer-cycle",
            layer,
            first,
            last,
            matrix: cloneMatrix(workingMatrix),
        });

        for (let offset = 0; offset < last - first; offset += 1) {
            const positions = {
                top: { row: first, col: first + offset },
                right: { row: first + offset, col: last },
                bottom: { row: last, col: last - offset },
                left: { row: last - offset, col: first },
            };

            const valuesBefore = {
                top: workingMatrix[positions.top.row][positions.top.col],
                right: workingMatrix[positions.right.row][positions.right.col],
                bottom:
                    workingMatrix[positions.bottom.row][positions.bottom.col],
                left: workingMatrix[positions.left.row][positions.left.col],
            };

            steps.push({
                type: "preview-cycle",
                algorithm: "layer-cycle",
                layer,
                first,
                last,
                offset,
                positions,
                valuesBefore,
                valuesAfter: {
                    top: valuesBefore.left,
                    right: valuesBefore.top,
                    bottom: valuesBefore.right,
                    left: valuesBefore.bottom,
                },
                matrix: cloneMatrix(workingMatrix),
                cycleIndex: totalCycles + 1,
            });

            workingMatrix[positions.top.row][positions.top.col] = valuesBefore.left;
            workingMatrix[positions.right.row][positions.right.col] =
                valuesBefore.top;
            workingMatrix[positions.bottom.row][positions.bottom.col] =
                valuesBefore.right;
            workingMatrix[positions.left.row][positions.left.col] =
                valuesBefore.bottom;

            totalCycles += 1;

            steps.push({
                type: "apply-cycle",
                algorithm: "layer-cycle",
                layer,
                first,
                last,
                offset,
                positions,
                valuesBefore,
                valuesAfter: {
                    top: workingMatrix[positions.top.row][positions.top.col],
                    right: workingMatrix[positions.right.row][positions.right.col],
                    bottom:
                        workingMatrix[positions.bottom.row][positions.bottom.col],
                    left: workingMatrix[positions.left.row][positions.left.col],
                },
                matrix: cloneMatrix(workingMatrix),
                cycleIndex: totalCycles,
            });
        }
    }

    steps.push({
        type: "complete",
        algorithm: "layer-cycle",
        matrix: cloneMatrix(workingMatrix),
    });

    return {
        steps,
        resultMatrix: cloneMatrix(workingMatrix),
        metadata: {
            algorithm: "layer-cycle",
            algorithmLabel: "4-Way Layer Cycle",
            totalMoves: totalCycles,
        },
    };
}

function buildRotateImagePlan(matrix, algorithm) {
    if (algorithm === "transpose-reflect") {
        return buildTransposeReflectSteps(matrix);
    }

    return buildLayerCycleSteps(matrix);
}

function createStepLogEntry(step, stepIndex, metadata) {
    const stepNumber = stepIndex + 1;

    if (step.type === "start") {
        return {
            id: `rotate-image-step-${stepNumber}-start`,
            stepIndex: stepNumber,
            kind: "Setup",
            title: "Initialize matrix",
            message: `The matrix starts unchanged. You can switch between the ring-based 4-way cycle method and the transpose plus reflect method.`,
        };
    }

    if (step.type === "algorithm-start" && step.algorithm === "layer-cycle") {
        return {
            id: `rotate-image-step-${stepNumber}-algorithm-layer`,
            stepIndex: stepNumber,
            kind: "Algorithm",
            title: "Use 4-way layer cycles",
            message: `This method rotates four cells at once inside each ring. It usually produces fewer animation steps because each cycle performs a larger in-place move.`,
        };
    }

    if (
        step.type === "algorithm-start" &&
        step.algorithm === "transpose-reflect"
    ) {
        return {
            id: `rotate-image-step-${stepNumber}-algorithm-transpose-reflect`,
            stepIndex: stepNumber,
            kind: "Algorithm",
            title: "Use transpose plus reflect",
            message: `This method first transposes across the main diagonal, then reflects each row horizontally. It matches the standard interview explanation.`,
        };
    }

    if (step.type === "phase-start" && step.phase === "transpose") {
        return {
            id: `rotate-image-step-${stepNumber}-phase-transpose`,
            stepIndex: stepNumber,
            kind: "Phase",
            title: "Start transpose",
            message: `Swap each cell above the main diagonal with its mirrored cell across the diagonal. Total transpose swaps: ${metadata.phaseTotals.transpose}.`,
        };
    }

    if (step.type === "phase-start" && step.phase === "reflect") {
        return {
            id: `rotate-image-step-${stepNumber}-phase-reflect`,
            stepIndex: stepNumber,
            kind: "Phase",
            title: "Start horizontal reflection",
            message: `Swap left and right partners inside each row. Total reflection swaps: ${metadata.phaseTotals.reflect}.`,
        };
    }

    if (step.type === "layer-start") {
        return {
            id: `rotate-image-step-${stepNumber}-layer-${step.layer}`,
            stepIndex: stepNumber,
            kind: "Layer",
            title: `Process layer ${step.layer}`,
            message: `Layer ${step.layer} spans indices ${step.first} through ${step.last}. Each offset in this ring performs one 4-way cycle.`,
        };
    }

    if (step.type === "preview-swap" && step.phase === "transpose") {
        return {
            id: `rotate-image-step-${stepNumber}-preview-transpose-${step.swapIndex}`,
            stepIndex: stepNumber,
            kind: "Preview",
            title: `Preview transpose swap ${step.swapIndex}`,
            message: `Swap ${formatCell(step.pair.a)} with ${formatCell(
                step.pair.b
            )}. Before the write, values are ${formatPairValues(
                step.valuesBefore
            )}.`,
        };
    }

    if (step.type === "apply-swap" && step.phase === "transpose") {
        return {
            id: `rotate-image-step-${stepNumber}-apply-transpose-${step.swapIndex}`,
            stepIndex: stepNumber,
            kind: "Write",
            title: `Apply transpose swap ${step.swapIndex}`,
            message: `The transpose write is complete. After the swap, values are ${formatPairValues(
                step.valuesAfter
            )}.`,
        };
    }

    if (step.type === "preview-swap" && step.phase === "reflect") {
        return {
            id: `rotate-image-step-${stepNumber}-preview-reflect-${step.swapIndex}`,
            stepIndex: stepNumber,
            kind: "Preview",
            title: `Preview reflect swap ${step.swapIndex}`,
            message: `Swap ${formatCell(step.pair.a)} with ${formatCell(
                step.pair.b
            )} inside the same row. Before the write, values are ${formatPairValues(
                step.valuesBefore
            )}.`,
        };
    }

    if (step.type === "apply-swap" && step.phase === "reflect") {
        return {
            id: `rotate-image-step-${stepNumber}-apply-reflect-${step.swapIndex}`,
            stepIndex: stepNumber,
            kind: "Write",
            title: `Apply reflect swap ${step.swapIndex}`,
            message: `The horizontal reflection write is complete. After the swap, values are ${formatPairValues(
                step.valuesAfter
            )}.`,
        };
    }

    if (step.type === "preview-cycle") {
        return {
            id: `rotate-image-step-${stepNumber}-preview-cycle-${step.cycleIndex}`,
            stepIndex: stepNumber,
            kind: "Preview",
            title: `Preview 4-way cycle ${step.cycleIndex}`,
            message: `Rotate the active ring cells in this order: top <- left, right <- top, bottom <- right, left <- bottom. Before the write, values are ${formatCycleValues(
                step.valuesBefore
            )}.`,
        };
    }

    if (step.type === "apply-cycle") {
        return {
            id: `rotate-image-step-${stepNumber}-apply-cycle-${step.cycleIndex}`,
            stepIndex: stepNumber,
            kind: "Write",
            title: `Apply 4-way cycle ${step.cycleIndex}`,
            message: `The 4-way ring write is complete. After the cycle, values are ${formatCycleValues(
                step.valuesAfter
            )}.`,
        };
    }

    return {
        id: `rotate-image-step-${stepNumber}-complete`,
        stepIndex: stepNumber,
        kind: "Result",
        title: "Rotation complete",
        message: `The matrix is now rotated 90 degrees clockwise in place using ${metadata.algorithmLabel}.`,
    };
}

export default class RotateImageScene extends BaseThreeScene {
    setup(props) {
        this.inputMatrix = normalizeMatrixInput(props.input);
        this.size = this.inputMatrix.length;
        this.algorithm = props.algorithm || "layer-cycle";

        const plan = buildRotateImagePlan(this.inputMatrix, this.algorithm);

        this.steps = plan.steps;
        this.resultMatrix = plan.resultMatrix;
        this.metadata = plan.metadata;

        this.currentMatrix = cloneMatrix(this.inputMatrix);
        this.currentPhase = "setup";
        this.currentPhaseLabel = "Setup";
        this.currentProgressLabel = "-";
        this.currentStatusLabel = "Initialize matrix";
        this.currentActiveCellsLabel = "-";
        this.currentValuesBeforeLabel = "-";
        this.currentValuesAfterLabel = "-";
        this.currentActiveCellMap = new Map();
        this.currentLayer = null;
        this.currentHighlightedRow = null;

        this.stepIndex = -1;
        this.currentStep = null;
        this.currentStepElapsed = 0;
        this.presentationTime = 0;

        this.scene.background = new THREE.Color(0x030712);
        this.scene.fog = new THREE.Fog(0x030712, 10, 34);

        this.cellSize = this.size <= 4 ? 1.32 : this.size === 5 ? 1.12 : 0.96;
        this.cellGap = this.size >= 6 ? 0.12 : 0.16;
        this.cellSpacing = this.cellSize + this.cellGap;
        this.boardSpan = (this.size - 1) * this.cellSpacing + this.cellSize;
        this.labelScale =
            this.size <= 4 ? [0.92, 0.46] : this.size === 5 ? [0.8, 0.4] : [0.68, 0.34];
        this.valueFontSize = this.size <= 4 ? 34 : this.size === 5 ? 30 : 24;

        this.camera.position.set(
            0,
            this.boardSpan * 1.18 + 2.8,
            this.boardSpan * 0.78 + 3.5
        );
        this.camera.lookAt(0, 0.25, 0);

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.08);
        this.scene.add(ambientLight);

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.6);
        keyLight.position.set(5, 8, 4);
        this.scene.add(keyLight);

        const fillLight = new THREE.PointLight(0x60a5fa, 18, 20, 2);
        fillLight.position.set(-4, 4, 4);
        this.scene.add(fillLight);

        const rimLight = new THREE.PointLight(0xa855f7, 16, 18, 2);
        rimLight.position.set(4, 5, -2);
        this.scene.add(rimLight);

        this.board = createMatrixBoardBase({
            width: this.boardSpan + 0.78,
            depth: this.boardSpan + 0.78,
            baseHeight: 0.46,
            baseY: -0.22,
            surfaceY: 0.02,
            baseColor: 0x081225,
            surfaceColor: 0x0c1b34,
            surfaceOpacity: 0.9,
        });
        this.scene.add(this.board);

        this.cellRoot = new THREE.Group();
        this.labelRoot = new THREE.Group();
        this.scene.add(this.cellRoot);
        this.scene.add(this.labelRoot);

        this.cellMeshes = new Map();
        this.cellLabels = new Map();
        this.cellFrames = new Map();
        this.swapBadges = new Map();
        this.cellPositions = new Map();
        this.rowLabels = [];
        this.colLabels = [];

        const originX = -((this.size - 1) * this.cellSpacing) * 0.5;
        const originZ = -((this.size - 1) * this.cellSpacing) * 0.5;

        for (let row = 0; row < this.size; row += 1) {
            for (let col = 0; col < this.size; col += 1) {
                const key = `${row},${col}`;
                const x = originX + col * this.cellSpacing;
                const z = originZ + row * this.cellSpacing;

                this.cellPositions.set(key, new THREE.Vector3(x, 0, z));

                const cellMesh = createSeatBlock({
                    occupied: false,
                    width: this.cellSize,
                    height: 0.24,
                    depth: this.cellSize,
                    emptyColor: BASE_CELL_COLOR.getHex(),
                    occupiedColor: BASE_CELL_COLOR.getHex(),
                });
                cellMesh.position.set(x, 0.14, z);
                cellMesh.userData.baseY = 0.14;
                this.cellMeshes.set(key, cellMesh);
                this.cellRoot.add(cellMesh);

                const cellLabel = createTextSprite({
                    text: String(this.inputMatrix[row][col]),
                    width: 132,
                    height: 76,
                    fontSize: this.valueFontSize,
                    textColor: "#f8fafc",
                    fillStyle: "rgba(15, 23, 42, 0.92)",
                    strokeStyle: "rgba(255, 255, 255, 0.08)",
                    outlineColor: "rgba(0, 0, 0, 0.95)",
                    outlineWidth: 6,
                    scale: this.labelScale,
                });
                cellLabel.position.set(x, 0.68, z);
                this.cellLabels.set(key, cellLabel);
                this.labelRoot.add(cellLabel);

                const cellFrame = createMatrixCellFrame(
                    this.cellSize + 0.12,
                    getPhaseColor(this.algorithm, "setup").getHex(),
                    0
                );
                cellFrame.position.set(x, 0.28, z);
                this.cellFrames.set(key, cellFrame);
                this.scene.add(cellFrame);

                const swapBadge = createTextSprite({
                    text: "",
                    width: 96,
                    height: 50,
                    fontSize: 22,
                    textColor: "#ffffff",
                    fillStyle: "rgba(15, 23, 42, 0.92)",
                    strokeStyle: "rgba(255, 255, 255, 0.08)",
                    outlineColor: "rgba(0, 0, 0, 0.95)",
                    outlineWidth: 5,
                    scale: this.size <= 5 ? [0.54, 0.26] : [0.46, 0.22],
                });
                swapBadge.position.set(x, 1.1, z);
                swapBadge.material.opacity = 0;
                this.swapBadges.set(key, swapBadge);
                this.labelRoot.add(swapBadge);
            }
        }

        for (let index = 0; index < this.size; index += 1) {
            const topX = originX + index * this.cellSpacing;
            const leftZ = originZ + index * this.cellSpacing;

            const colLabel = createTextSprite({
                text: String(index),
                width: 90,
                height: 48,
                fontSize: 22,
                textColor: "#bfdbfe",
                fillStyle: "rgba(2, 6, 23, 0.76)",
                strokeStyle: "rgba(255, 255, 255, 0.04)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 5,
                scale: [0.5, 0.22],
            });
            colLabel.position.set(
                topX,
                0.48,
                originZ - this.cellSpacing * 0.82
            );
            this.colLabels.push(colLabel);
            this.labelRoot.add(colLabel);

            const rowLabel = createTextSprite({
                text: String(index),
                width: 90,
                height: 48,
                fontSize: 22,
                textColor: "#bfdbfe",
                fillStyle: "rgba(2, 6, 23, 0.76)",
                strokeStyle: "rgba(255, 255, 255, 0.04)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 5,
                scale: [0.5, 0.22],
            });
            rowLabel.position.set(
                originX - this.cellSpacing * 0.82,
                0.48,
                leftZ
            );
            this.rowLabels.push(rowLabel);
            this.labelRoot.add(rowLabel);
        }

        this.boardFrameBaseSize = this.boardSpan + 0.18;
        this.boardFrame = createMatrixCellFrame(
            this.boardFrameBaseSize,
            getPhaseColor(this.algorithm, "setup").getHex(),
            0
        );
        this.boardFrame.position.set(0, 0.24, 0);
        this.scene.add(this.boardFrame);

        this.resetAnimationState();
        this.advanceStep();
    }

    getCellCenter(row, col) {
        return this.cellPositions.get(`${row},${col}`);
    }

    getLayerCenter(first, last) {
        const topLeft = this.getCellCenter(first, first);
        const bottomRight = this.getCellCenter(last, last);

        return new THREE.Vector3(
            (topLeft.x + bottomRight.x) * 0.5,
            0.24,
            (topLeft.z + bottomRight.z) * 0.5
        );
    }

    getLayerFrameSize(first, last) {
        return (last - first) * this.cellSpacing + this.cellSize + 0.18;
    }

    resetAnimationState() {
        this.stepIndex = -1;
        this.currentStep = null;
        this.currentStepElapsed = 0;
        this.presentationTime = 0;
        this.currentMatrix = cloneMatrix(this.inputMatrix);
        this.currentPhase = "setup";
        this.currentPhaseLabel = "Setup";
        this.currentProgressLabel = "-";
        this.currentStatusLabel = "Initialize matrix";
        this.currentActiveCellsLabel = "-";
        this.currentValuesBeforeLabel = "-";
        this.currentValuesAfterLabel = "-";
        this.currentActiveCellMap = new Map();
        this.currentLayer = null;
        this.currentHighlightedRow = null;

        this.resetStepLog([]);
        this.replaceUiState({
            algorithmLabel: this.metadata.algorithmLabel,
            matrixSizeLabel: `${this.size} x ${this.size}`,
            phaseLabel: this.currentPhaseLabel,
            progressLabel: this.currentProgressLabel,
            statusLabel: this.currentStatusLabel,
            activeCellsLabel: this.currentActiveCellsLabel,
            valuesBeforeLabel: this.currentValuesBeforeLabel,
            valuesAfterLabel: this.currentValuesAfterLabel,
        });
        this.syncPlaybackState();
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        });
    }

    syncUiState() {
        this.replaceUiState({
            algorithmLabel: this.metadata.algorithmLabel,
            matrixSizeLabel: `${this.size} x ${this.size}`,
            phaseLabel: this.currentPhaseLabel,
            progressLabel: this.currentProgressLabel,
            statusLabel: this.currentStatusLabel,
            activeCellsLabel: this.currentActiveCellsLabel,
            valuesBeforeLabel: this.currentValuesBeforeLabel,
            valuesAfterLabel: this.currentValuesAfterLabel,
        });
    }

    getStepDuration(step) {
        if (step.type === "start") {
            return 1.05;
        }

        if (step.type === "algorithm-start") {
            return 1.0;
        }

        if (step.type === "phase-start" || step.type === "layer-start") {
            return 0.9;
        }

        if (step.type === "preview-swap" || step.type === "preview-cycle") {
            return 1.0;
        }

        if (step.type === "apply-swap" || step.type === "apply-cycle") {
            return 1.0;
        }

        return 2.1;
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1;

        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState();
            this.syncUiState();
            return false;
        }

        this.stepIndex = nextStepIndex;
        this.currentStepElapsed = 0;
        this.currentStep = this.steps[this.stepIndex] || null;
        this.applyStep(this.currentStep);
        this.appendStepLog(
            createStepLogEntry(this.currentStep, this.stepIndex, this.metadata)
        );
        this.syncPlaybackState();
        this.syncUiState();

        return true;
    }

    applyStep(step) {
        if (!step) {
            return;
        }

        this.currentMatrix = cloneMatrix(step.matrix || this.currentMatrix);

        if (step.type === "start") {
            this.currentPhase = "setup";
            this.currentPhaseLabel = "Setup";
            this.currentProgressLabel = "-";
            this.currentStatusLabel = "Initialize matrix";
            this.currentActiveCellsLabel = "-";
            this.currentValuesBeforeLabel = "-";
            this.currentValuesAfterLabel = "-";
            this.currentActiveCellMap = new Map();
            this.currentLayer = null;
            this.currentHighlightedRow = null;
            return;
        }

        if (step.type === "algorithm-start") {
            this.currentPhase =
                step.algorithm === "transpose-reflect"
                    ? "transpose"
                    : "layer-cycle";
            this.currentPhaseLabel =
                step.algorithm === "transpose-reflect"
                    ? "Transpose + Reflect"
                    : "4-Way Layer Cycle";
            this.currentProgressLabel = "Choose a move";
            this.currentStatusLabel =
                step.algorithm === "transpose-reflect"
                    ? "Prepare diagonal transpose, then row reflection"
                    : "Prepare ring-based 4-way cycles";
            this.currentActiveCellsLabel = "-";
            this.currentValuesBeforeLabel = "-";
            this.currentValuesAfterLabel = "-";
            this.currentActiveCellMap = new Map();
            this.currentLayer = step.algorithm === "layer-cycle" ? 0 : null;
            this.currentHighlightedRow = null;
            return;
        }

        if (step.type === "phase-start") {
            this.currentPhase = step.phase;
            this.currentPhaseLabel =
                step.phase === "transpose" ? "Transpose" : "Reflect";
            this.currentProgressLabel =
                step.phase === "transpose"
                    ? `0 / ${this.metadata.phaseTotals.transpose}`
                    : `0 / ${this.metadata.phaseTotals.reflect}`;
            this.currentStatusLabel =
                step.phase === "transpose"
                    ? "Swap across the main diagonal"
                    : "Swap each row's left and right partners";
            this.currentActiveCellsLabel = "-";
            this.currentValuesBeforeLabel = "-";
            this.currentValuesAfterLabel = "-";
            this.currentActiveCellMap = new Map();
            this.currentLayer = null;
            this.currentHighlightedRow = null;
            return;
        }

        if (step.type === "layer-start") {
            this.currentPhase = "layer-cycle";
            this.currentPhaseLabel = `Layer ${step.layer}`;
            this.currentProgressLabel = `0 / ${this.metadata.totalMoves}`;
            this.currentStatusLabel = `Scan ring ${step.layer}`;
            this.currentActiveCellsLabel = "-";
            this.currentValuesBeforeLabel = "-";
            this.currentValuesAfterLabel = "-";
            this.currentActiveCellMap = new Map();
            this.currentLayer = step.layer;
            this.currentHighlightedRow = null;
            return;
        }

        if (step.type === "preview-swap" || step.type === "apply-swap") {
            this.currentPhase = step.phase;
            this.currentPhaseLabel =
                step.phase === "transpose" ? "Transpose" : "Reflect";
            this.currentProgressLabel =
                step.phase === "transpose"
                    ? `${step.swapIndex} / ${this.metadata.phaseTotals.transpose}`
                    : `${step.swapIndex} / ${this.metadata.phaseTotals.reflect}`;
            this.currentStatusLabel =
                step.type === "preview-swap"
                    ? step.phase === "transpose"
                        ? `Preview transpose swap ${step.swapIndex}`
                        : `Preview reflect swap ${step.swapIndex}`
                    : step.phase === "transpose"
                        ? `Apply transpose swap ${step.swapIndex}`
                        : `Apply reflect swap ${step.swapIndex}`;
            this.currentActiveCellsLabel = formatPair(step.pair);
            this.currentValuesBeforeLabel = formatPairValues(step.valuesBefore);
            this.currentValuesAfterLabel = formatPairValues(step.valuesAfter);
            this.currentActiveCellMap = createActiveMapForPair(step.pair);
            this.currentLayer = null;
            this.currentHighlightedRow =
                step.phase === "reflect" ? step.row : null;
            return;
        }

        if (step.type === "preview-cycle" || step.type === "apply-cycle") {
            this.currentPhase = "layer-cycle";
            this.currentPhaseLabel = `Layer ${step.layer}`;
            this.currentProgressLabel = `${step.cycleIndex} / ${this.metadata.totalMoves}`;
            this.currentStatusLabel =
                step.type === "preview-cycle"
                    ? `Preview cycle ${step.cycleIndex}`
                    : `Apply cycle ${step.cycleIndex}`;
            this.currentActiveCellsLabel = formatCycleCells(step.positions);
            this.currentValuesBeforeLabel = formatCycleValues(step.valuesBefore);
            this.currentValuesAfterLabel = formatCycleValues(step.valuesAfter);
            this.currentActiveCellMap = createActiveMapForCycle(step.positions);
            this.currentLayer = step.layer;
            this.currentHighlightedRow = null;
            return;
        }

        this.currentPhase = "complete";
        this.currentPhaseLabel = "Complete";
        this.currentProgressLabel =
            this.algorithm === "transpose-reflect"
                ? `${this.metadata.totalMoves} total swaps`
                : `${this.metadata.totalMoves} total cycles`;
        this.currentStatusLabel = "Rotation complete";
        this.currentActiveCellsLabel = "-";
        this.currentValuesBeforeLabel = "-";
        this.currentValuesAfterLabel = "-";
        this.currentActiveCellMap = new Map();
        this.currentLayer = null;
        this.currentHighlightedRow = null;
    }

    onResetPlayback() {
        this.resetAnimationState();
        this.advanceStep();
    }

    onStepForward() {
        this.advanceStep();
    }

    update(delta) {
        if (!this.currentStep) {
            return;
        }

        const playbackDelta = this.isPlaying ? delta : 0;
        this.presentationTime += playbackDelta;
        this.currentStepElapsed += playbackDelta;

        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep();

            if (!advanced) {
                this.pause();
            }
        }

        this.updateBoardFrame();
        this.updateCells(this.presentationTime);
    }

    updateBoardFrame() {
        let targetOpacity = 0;
        let targetColor = getPhaseColor(this.algorithm, this.currentPhase);
        let targetPosition = new THREE.Vector3(0, 0.24, 0);
        let targetScale = 1;

        if (this.currentPhase === "complete") {
            targetOpacity = 0.95;
            targetColor = COMPLETE_CELL_COLOR;
        } else if (this.algorithm === "layer-cycle") {
            if (this.currentLayer !== null) {
                const first = this.currentLayer;
                const last = this.size - 1 - this.currentLayer;
                targetOpacity = 0.92;
                targetPosition = this.getLayerCenter(first, last);
                targetScale =
                    this.getLayerFrameSize(first, last) / this.boardFrameBaseSize;
                targetColor = LAYER_PHASE_COLOR;
            }
        } else if (
            this.currentPhase === "transpose" ||
            this.currentPhase === "reflect"
        ) {
            targetOpacity = 0.88;
        }

        this.boardFrame.position.lerp(targetPosition, 0.18);
        this.boardFrame.scale.x = THREE.MathUtils.lerp(
            this.boardFrame.scale.x,
            targetScale,
            0.18
        );
        this.boardFrame.scale.z = THREE.MathUtils.lerp(
            this.boardFrame.scale.z,
            targetScale,
            0.18
        );
        this.boardFrame.material.color.lerp(targetColor, 0.16);
        this.boardFrame.material.opacity = THREE.MathUtils.lerp(
            this.boardFrame.material.opacity,
            targetOpacity,
            0.18
        );
    }

    updateCells(elapsed) {
        const phaseColor = getPhaseColor(this.algorithm, this.currentPhase);

        for (let row = 0; row < this.size; row += 1) {
            for (let col = 0; col < this.size; col += 1) {
                const key = `${row},${col}`;
                const cellMesh = this.cellMeshes.get(key);
                const cellLabel = this.cellLabels.get(key);
                const cellFrame = this.cellFrames.get(key);
                const swapBadge = this.swapBadges.get(key);
                const value = this.currentMatrix[row][col];
                const activeTag = this.currentActiveCellMap.get(key) || null;
                const isActive = activeTag !== null;

                let isPhaseHighlighted = false;

                if (
                    this.algorithm === "transpose-reflect" &&
                    this.currentPhase === "transpose" &&
                    row === col
                ) {
                    isPhaseHighlighted = true;
                }

                if (
                    this.algorithm === "transpose-reflect" &&
                    this.currentPhase === "reflect" &&
                    this.currentHighlightedRow !== null &&
                    row === this.currentHighlightedRow
                ) {
                    isPhaseHighlighted = true;
                }

                if (
                    this.algorithm === "layer-cycle" &&
                    this.currentLayer !== null
                ) {
                    const first = this.currentLayer;
                    const last = this.size - 1 - this.currentLayer;

                    if (
                        row >= first &&
                        row <= last &&
                        col >= first &&
                        col <= last &&
                        (row === first ||
                            row === last ||
                            col === first ||
                            col === last)
                    ) {
                        isPhaseHighlighted = true;
                    }
                }

                let targetColor = BASE_CELL_COLOR.clone();
                let targetScale = 1;
                let targetEmissiveIntensity = 0.14;
                let labelFill = "rgba(15, 23, 42, 0.92)";

                if (this.currentPhase === "complete") {
                    targetColor = COMPLETE_CELL_COLOR.clone().lerp(
                        BASE_CELL_COLOR,
                        0.22
                    );
                    targetEmissiveIntensity = 0.2;
                } else if (isPhaseHighlighted) {
                    targetColor = BASE_CELL_COLOR.clone().lerp(phaseColor, 0.45);
                    targetEmissiveIntensity = 0.2;
                }

                if (isActive) {
                    const activeColor = getTagColor(activeTag);
                    targetColor = activeColor.clone();
                    targetScale =
                        1.08 + Math.sin(elapsed * 5.5 + row + col) * 0.015;
                    targetEmissiveIntensity = 0.48;
                    labelFill = `rgba(${Math.round(
                        activeColor.r * 255
                    )}, ${Math.round(activeColor.g * 255)}, ${Math.round(
                        activeColor.b * 255
                    )}, 0.96)`;
                }

                cellMesh.material.color.lerp(targetColor, 0.16);
                cellMesh.material.emissive.lerp(targetColor, 0.16);
                cellMesh.material.emissiveIntensity = THREE.MathUtils.lerp(
                    cellMesh.material.emissiveIntensity,
                    targetEmissiveIntensity,
                    0.16
                );

                const nextScale = THREE.MathUtils.lerp(
                    cellMesh.scale.x,
                    targetScale,
                    0.16
                );

                cellMesh.scale.set(
                    nextScale,
                    THREE.MathUtils.lerp(cellMesh.scale.y, targetScale, 0.16),
                    nextScale
                );

                cellMesh.position.y = THREE.MathUtils.lerp(
                    cellMesh.position.y,
                    cellMesh.userData.baseY +
                        (isActive
                            ? Math.sin(elapsed * 5.5 + row + col) * 0.03
                            : 0),
                    0.18
                );

                updateTextSprite(cellLabel, String(value), {
                    fontSize: this.valueFontSize,
                    textColor: "#f8fafc",
                    fillStyle: labelFill,
                    strokeStyle: "rgba(255, 255, 255, 0.08)",
                    outlineColor: "rgba(0, 0, 0, 0.95)",
                    outlineWidth: 6,
                });

                cellLabel.position.y = THREE.MathUtils.lerp(
                    cellLabel.position.y,
                    cellMesh.position.y + 0.54,
                    0.18
                );

                let frameOpacity = 0;
                let frameColor = phaseColor;

                if (isPhaseHighlighted && this.currentPhase !== "complete") {
                    frameOpacity = 0.28;
                }

                if (this.currentPhase === "complete") {
                    frameColor = COMPLETE_CELL_COLOR;
                    frameOpacity = 0.2;
                }

                if (isActive) {
                    frameColor = getTagColor(activeTag);
                    frameOpacity = 0.98;
                }

                cellFrame.material.color.lerp(frameColor, 0.16);
                cellFrame.material.opacity = THREE.MathUtils.lerp(
                    cellFrame.material.opacity,
                    frameOpacity,
                    0.16
                );
                cellFrame.position.y = THREE.MathUtils.lerp(
                    cellFrame.position.y,
                    cellMesh.position.y + 0.14,
                    0.18
                );

                if (isActive) {
                    const badgeColor = getTagColor(activeTag);

                    updateTextSprite(swapBadge, activeTag, {
                        fontSize: 22,
                        textColor: "#ffffff",
                        fillStyle: `rgba(${Math.round(
                            badgeColor.r * 255
                        )}, ${Math.round(badgeColor.g * 255)}, ${Math.round(
                            badgeColor.b * 255
                        )}, 0.94)`,
                        strokeStyle: "rgba(255, 255, 255, 0.08)",
                        outlineColor: "rgba(0, 0, 0, 0.95)",
                        outlineWidth: 5,
                    });

                    swapBadge.material.opacity = THREE.MathUtils.lerp(
                        swapBadge.material.opacity,
                        0.96,
                        0.16
                    );
                } else {
                    swapBadge.material.opacity = THREE.MathUtils.lerp(
                        swapBadge.material.opacity,
                        0,
                        0.16
                    );
                }

                swapBadge.position.y = THREE.MathUtils.lerp(
                    swapBadge.position.y,
                    cellMesh.position.y + 0.95,
                    0.18
                );
            }
        }
    }
}